import {
  AssignedWorkflowPhaseDto,
  PendingItemDto,
  ScheduledEventDto,
} from '@act/shared/data-transfer-objects';
import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { selectMembersMap } from '../../slices/memberSlice';
import { selectUnAuditedChangeRequests } from '../../slices/memberStatusChangeRequestSlice';
import { selectPendingMemberTransferRequests } from '../../slices/memberTransferRequestSlice';
import {
  selectGuideScheduledEventLoadingStatus,
  selectScheduledEvents,
  selectScheduledEventsByMember,
} from '../../slices/scheduledEventSlice';
import { selectTodoSliceStatus } from '../../slices/todoSlice';
import { selectAvailableWorkflowPhases } from '../../slices/workflowSlice';
import { useAppSelector } from '../../store-hooks';
import { RootState } from 'redux/reemaStore';
import { GuideSelectorParams } from '../../types/selector-types';

export type PendingItemFilters = 'call' | 'message' | 'visit' | 'task';
export const filterChoices: { label: string; value: PendingItemFilters }[] = [
  { label: 'Calls', value: 'call' },
  { label: 'Texts', value: 'message' },
  { label: 'Visits', value: 'visit' },
  { label: 'Tasks', value: 'task' },
];

export const isPendingItemInFilter = (
  pendingItem: PendingItemDto,
  filters: PendingItemFilters[]
) => {
  if (!filters?.length) {
    return true;
  } else if (
    pendingItem.externalItemType !== 'scheduled_event' &&
    filters.includes('task')
  ) {
    return true;
  } else if (
    pendingItem.externalItemType === 'scheduled_event' &&
    filters.includes(pendingItem.eventType as PendingItemFilters)
  ) {
    return true;
  }

  return false;
};

const convertScheduledEvent: (ScheduledEventDto) => PendingItemDto = se => {
  const timeFromDate = DateTime.fromFormat(se.date, 'yyyy-MM-dd')
    .endOf('day')
    .toMillis();
  const timestamp = se.time || timeFromDate;
  return {
    externalItemId: se.id,
    externalItemType: 'scheduled_event',
    timestamp,
    eventType: se.type,
    memberId: se.userId,
  };
};

interface MemberPendingSelectorParams {
  memberId: string;
  filters: PendingItemFilters[];
  [key: string]: unknown;
}

// input: {memberId, filters}
export const selectPendingItemsForMember: (
  state,
  paramObj: MemberPendingSelectorParams
) => PendingItemDto[] = createSelector(
  [
    selectAvailableWorkflowPhases,
    selectScheduledEventsByMember,
    (_state: RootState, paramObj: MemberPendingSelectorParams) =>
      paramObj.filters,
  ],
  (
    workflowPhases: AssignedWorkflowPhaseDto[],
    scheduledEvents: ScheduledEventDto[],
    filters: PendingItemFilters[]
  ): PendingItemDto[] => {
    const pendingItems: PendingItemDto[] = [];
    const stringFilters = filters.map(a => a as string);
    const noFilters = !filters?.length;
    pendingItems.push(
      ...scheduledEvents
        .filter(se => noFilters || stringFilters.includes(se.type as string))
        .map(convertScheduledEvent)
    );
    if (noFilters || filters.includes('task')) {
      pendingItems.push(
        ...workflowPhases.map((wp): PendingItemDto => {
          return {
            externalItemId: wp.id,
            externalItemType: 'workflow_phase',
            timestamp: wp.dueDate,
            eventType: 'workflow_phase',
            memberId: wp.userId,
          };
        })
      );
    }
    pendingItems.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
    return pendingItems;
  }
);


export const selectScheduledEventsByGuide: (
  state,
  paramObj: GuideSelectorParams
) => ScheduledEventDto[] = createSelector(
  [
    selectScheduledEvents,
    selectMembersMap,
    (state, paramObj: GuideSelectorParams) => paramObj.guideId,
  ],
  (scheduledEvents, memberMap, guideId) => {
    return scheduledEvents.filter(
      s => memberMap[s.userId]?.assignedGuideId === guideId
    );
  }
);

export const selectLoadingPendingItemsForGuide = createSelector(
  [selectGuideScheduledEventLoadingStatus, selectTodoSliceStatus],
  (scheduledEventsLoadingStatus, todosloadingStatus) => {
    return (
      scheduledEventsLoadingStatus === 'loading' ||
      todosloadingStatus === 'loading'
    );
  }
);

export const selectPendingItemsForGuide: (
  state,
  paramObj: GuideSelectorParams
) => PendingItemDto[] = createSelector(
  [
    selectScheduledEventsByGuide,
    selectPendingMemberTransferRequests,
    selectUnAuditedChangeRequests,
  ],
  (scheduledEvents, transfers, statusChanges) => {
    const pendingItems: PendingItemDto[] = [];
    pendingItems.push(...scheduledEvents.map(convertScheduledEvent));
    pendingItems.push(
      ...transfers.map((t): PendingItemDto => {
        return {
          externalItemId: t.id,
          externalItemType: 'transfer_request',
          timestamp: null,
          eventType: 'transfer_request',
          memberId: t.memberId,
        };
      })
    );
    pendingItems.push(
      ...statusChanges.map((sc): PendingItemDto => {
        return {
          externalItemId: sc.id,
          externalItemType: 'status_change_request',
          timestamp: null,
          eventType: 'status_change_request',
          memberId: sc.memberId,
        };
      })
    );
    return pendingItems;
  }
);

export const usePendingItemSelector = {
  pendingItemsForMember: (memberId: string, filters: PendingItemFilters[]) =>
    useAppSelector(state =>
      selectPendingItemsForMember(state, { memberId, filters })
    ),
};
